import { Command } from 'commander';
import { TaskTemplateManager } from '../core/task-template';
import { WorkflowManager } from '../core/workflow-manager';
import { logger } from '../utils/logger';
import { withErrorHandling } from '../utils/error-handler';
import pc from 'picocolors';
import inquirer from 'inquirer';
import { Table } from 'console-table-printer';

export function createTemplateCommand(): Command {
  const command = new Command('template');
  
  command
    .description('Task template management')
    .alias('tpl');

  // List templates
  command
    .command('list')
    .description('List available task templates')
    .option('-c, --category <category>', 'Filter by category')
    .option('-t, --tag <tag>', 'Filter by tag')
    .action(withErrorHandling(async (options) => {
      const templateManager = new TaskTemplateManager();
      await templateManager.initialize();
      
      let templates = templateManager.getAllTemplates();
      
      // Apply filters
      if (options.category) {
        templates = templateManager.getTemplatesByCategory(options.category);
      }
      
      if (options.tag) {
        templates = templates.filter(t => 
          t.tags?.includes(options.tag)
        );
      }
      
      if (templates.length === 0) {
        console.log(pc.yellow('No templates found'));
        return;
      }
      
      // Group by category
      const categories = templateManager.getAllCategories();
      
      for (const category of categories) {
        const categoryTemplates = templates.filter(t => t.category === category.id);
        if (categoryTemplates.length === 0) continue;
        
        console.log(pc.bold(`\n${category.icon || '📁'} ${category.name}`));
        if (category.description) {
          console.log(pc.dim(category.description));
        }
        console.log();
        
        const table = new Table({
          columns: [
            { name: 'id', title: 'ID', alignment: 'left' },
            { name: 'name', title: 'Name', alignment: 'left' },
            { name: 'time', title: 'Est. Time', alignment: 'right' },
            { name: 'priority', title: 'Priority', alignment: 'center' },
            { name: 'tags', title: 'Tags', alignment: 'left' }
          ]
        });
        
        categoryTemplates.forEach(template => {
          table.addRow({
            id: pc.cyan(template.id),
            name: template.name,
            time: formatTime(template.estimatedTime || 0),
            priority: formatPriority(template.priority || 'medium'),
            tags: template.tags?.join(', ') || ''
          });
        });
        
        table.printTable();
      }
    }));

  // Show template details
  command
    .command('show')
    .description('Show template details')
    .argument('<id>', 'Template ID')
    .action(withErrorHandling(async (id) => {
      const templateManager = new TaskTemplateManager();
      await templateManager.initialize();
      
      const template = templateManager.getTemplate(id);
      if (!template) {
        console.log(pc.red(`Template not found: ${id}`));
        return;
      }
      
      console.log(pc.bold(`\n📋 Template: ${template.name}\n`));
      
      if (template.description) {
        console.log(pc.dim(template.description));
        console.log();
      }
      
      // Basic info
      console.log(`ID: ${pc.cyan(template.id)}`);
      console.log(`Category: ${template.category}`);
      console.log(`Priority: ${formatPriority(template.priority || 'medium')}`);
      console.log(`Estimated Time: ${formatTime(template.estimatedTime || 0)}`);
      
      if (template.tags && template.tags.length > 0) {
        console.log(`Tags: ${template.tags.map(t => pc.dim(`#${t}`)).join(' ')}`);
      }
      
      // Variables
      if (template.variables && Object.keys(template.variables).length > 0) {
        console.log(pc.bold('\n📝 Variables:'));
        Object.entries(template.variables).forEach(([key, value]) => {
          console.log(`  ${key}: ${JSON.stringify(value)}`);
        });
      }
      
      // Subtasks
      if (template.subtasks && template.subtasks.length > 0) {
        console.log(pc.bold('\n🔗 Subtasks:'));
        
        template.subtasks.forEach((subtask, index) => {
          const deps = subtask.dependencies?.length ? 
            pc.dim(` [depends on: ${subtask.dependencies.join(', ')}]`) : '';
          
          console.log(`\n${index + 1}. ${subtask.title}${deps}`);
          if (subtask.description) {
            console.log(pc.dim(`   ${subtask.description}`));
          }
          console.log(`   Type: ${subtask.type}, Priority: ${subtask.priority}`);
          console.log(`   Time: ${formatTime(subtask.estimatedTime)}`);
        });
      }
      
      // Metadata
      if (template.metadata) {
        console.log(pc.bold('\n📊 Metadata:'));
        if (template.metadata.author) {
          console.log(`  Author: ${template.metadata.author}`);
        }
        if (template.metadata.version) {
          console.log(`  Version: ${template.metadata.version}`);
        }
        if (template.metadata.updated) {
          console.log(`  Updated: ${new Date(template.metadata.updated).toLocaleDateString()}`);
        }
      }
    }));

  // Use template
  command
    .command('use')
    .description('Create task from template')
    .argument('<id>', 'Template ID')
    .option('--var <key=value...>', 'Template variables')
    .option('-s, --start', 'Start task immediately')
    .option('--save', 'Save generated task')
    .action(withErrorHandling(async (id, options) => {
      const templateManager = new TaskTemplateManager();
      await templateManager.initialize();
      
      const template = templateManager.getTemplate(id);
      if (!template) {
        console.log(pc.red(`Template not found: ${id}`));
        return;
      }
      
      // Parse variables
      const variables: Record<string, any> = {};
      if (options.var) {
        for (const varStr of options.var) {
          const [key, value] = varStr.split('=');
          if (key && value) {
            variables[key] = value;
          }
        }
      }
      
      // Create task from template
      const task = templateManager.createTaskFromTemplate(id, variables);
      
      console.log(pc.bold(`\n✨ Created task from template: ${template.name}\n`));
      console.log(`Task: ${pc.cyan(task.content)}`);
      console.log(`Priority: ${formatPriority(task.priority)}`);
      
      if (task.subtasks) {
        console.log(`Subtasks: ${task.subtasks.length}`);
        
        // Show subtask summary
        console.log(pc.bold('\n📋 Task Breakdown:'));
        task.subtasks.forEach((subtask, index) => {
          console.log(`  ${index + 1}. ${subtask.title} (${formatTime(subtask.estimatedTime)})`);
        });
        
        const totalTime = task.subtasks.reduce((sum, st) => sum + st.estimatedTime, 0);
        console.log(pc.bold(`\nTotal Time: ${formatTime(totalTime)}`));
      }
      
      // Start task if requested
      if (options.start) {
        const manager = new WorkflowManager();
        await manager.startTask({
          task: task.content,
          priority: task.priority,
          phase: 'understanding'
        });
        
        console.log(pc.green('\n✅ Task started'));
      }
      
      // Save if requested
      if (options.save && task.subtasks) {
        const manager = new WorkflowManager();
        const splitResult = {
          originalTask: task,
          totalTime: task.subtasks.reduce((sum, st) => sum + st.estimatedTime, 0),
          taskCount: task.subtasks.length,
          maxDepth: 1,
          subtasks: task.subtasks,
          criticalPath: []
        };
        
        await manager.saveSubtasks(splitResult);
        console.log(pc.green('\n✅ Subtasks saved'));
      }
    }));

  // Create custom template
  command
    .command('create')
    .description('Create a custom template')
    .action(withErrorHandling(async () => {
      const templateManager = new TaskTemplateManager();
      await templateManager.initialize();
      
      // Interactive template creation
      const answers = await inquirer.prompt([
        {
          type: 'input',
          name: 'id',
          message: 'Template ID:',
          validate: (input) => {
            if (!input) return 'ID is required';
            if (templateManager.getTemplate(input)) {
              return 'Template with this ID already exists';
            }
            return true;
          }
        },
        {
          type: 'input',
          name: 'name',
          message: 'Template name:',
          validate: (input) => input ? true : 'Name is required'
        },
        {
          type: 'input',
          name: 'description',
          message: 'Description (optional):'
        },
        {
          type: 'list',
          name: 'category',
          message: 'Category:',
          choices: templateManager.getAllCategories().map(c => ({
            name: `${c.icon || '📁'} ${c.name}`,
            value: c.id
          }))
        },
        {
          type: 'list',
          name: 'priority',
          message: 'Default priority:',
          choices: ['high', 'medium', 'low']
        },
        {
          type: 'input',
          name: 'tags',
          message: 'Tags (comma-separated):'
        },
        {
          type: 'number',
          name: 'estimatedTime',
          message: 'Estimated time (minutes):',
          default: 60
        }
      ]);
      
      // Ask about subtasks
      const { hasSubtasks } = await inquirer.prompt({
        type: 'confirm',
        name: 'hasSubtasks',
        message: 'Add subtasks?',
        default: true
      });
      
      const subtasks = [];
      if (hasSubtasks) {
        let addMore = true;
        let subtaskId = 1;
        
        while (addMore) {
          console.log(pc.dim(`\nSubtask #${subtaskId}:`));
          
          const subtask = await inquirer.prompt([
            {
              type: 'input',
              name: 'title',
              message: 'Title:',
              validate: (input) => input ? true : 'Title is required'
            },
            {
              type: 'list',
              name: 'type',
              message: 'Type:',
              choices: [
                'feature', 'bugfix', 'documentation', 'testing',
                'performance', 'security', 'devops', 'research'
              ]
            },
            {
              type: 'list',
              name: 'priority',
              message: 'Priority:',
              choices: ['high', 'medium', 'low']
            },
            {
              type: 'number',
              name: 'estimatedTime',
              message: 'Estimated time (minutes):',
              default: 30
            }
          ]);
          
          subtasks.push({
            id: subtaskId.toString(),
            ...subtask
          });
          
          const { more } = await inquirer.prompt({
            type: 'confirm',
            name: 'more',
            message: 'Add another subtask?',
            default: true
          });
          
          addMore = more;
          subtaskId++;
        }
      }
      
      // Create template
      const template = {
        id: answers.id,
        name: answers.name,
        description: answers.description || undefined,
        category: answers.category,
        priority: answers.priority,
        tags: answers.tags ? answers.tags.split(',').map(t => t.trim()) : [],
        estimatedTime: answers.estimatedTime,
        subtasks: subtasks.length > 0 ? subtasks : undefined,
        metadata: {
          author: process.env.USER || 'unknown',
          version: '1.0.0',
          created: new Date()
        }
      };
      
      // Save template
      await templateManager.saveTemplate(template);
      
      console.log(pc.green(`\n✅ Template created: ${template.id}`));
      console.log(pc.dim('Use "workflow template use ' + template.id + '" to create tasks from this template'));
    }));

  // Delete template
  command
    .command('delete')
    .description('Delete a custom template')
    .argument('<id>', 'Template ID')
    .option('-f, --force', 'Skip confirmation')
    .action(withErrorHandling(async (id, options) => {
      const templateManager = new TaskTemplateManager();
      await templateManager.initialize();
      
      const template = templateManager.getTemplate(id);
      if (!template) {
        console.log(pc.red(`Template not found: ${id}`));
        return;
      }
      
      if (!options.force) {
        const { confirm } = await inquirer.prompt({
          type: 'confirm',
          name: 'confirm',
          message: `Delete template "${template.name}"?`,
          default: false
        });
        
        if (!confirm) {
          console.log(pc.yellow('Cancelled'));
          return;
        }
      }
      
      await templateManager.deleteTemplate(id);
      console.log(pc.green(`✅ Template deleted: ${id}`));
    }));

  // Search templates
  command
    .command('search')
    .description('Search templates')
    .argument('<query>', 'Search query')
    .action(withErrorHandling(async (query) => {
      const templateManager = new TaskTemplateManager();
      await templateManager.initialize();
      
      const results = templateManager.searchTemplates(query);
      
      if (results.length === 0) {
        console.log(pc.yellow(`No templates found for: ${query}`));
        return;
      }
      
      console.log(pc.bold(`\n🔍 Found ${results.length} templates:\n`));
      
      const table = new Table({
        columns: [
          { name: 'id', title: 'ID', alignment: 'left' },
          { name: 'name', title: 'Name', alignment: 'left' },
          { name: 'category', title: 'Category', alignment: 'left' },
          { name: 'tags', title: 'Tags', alignment: 'left' }
        ]
      });
      
      results.forEach(template => {
        table.addRow({
          id: pc.cyan(template.id),
          name: template.name,
          category: template.category,
          tags: template.tags?.join(', ') || ''
        });
      });
      
      table.printTable();
    }));

  return command;
}

// Helper functions
function formatTime(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

function formatPriority(priority: string): string {
  const colors = {
    high: pc.red,
    medium: pc.yellow,
    low: pc.green
  };
  return colors[priority]?.(priority) || priority;
}